// Wrapper that posts feedback to BOTH Firebase (AIDELINE) AND Echo-Brain
import { feedbackService } from './feedbackService';
import { echoBrainService } from './echoBrainService';
import { FeedbackType, FeedbackCategory, FeedbackPriority } from '../types';

const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  bug: 'Bug',
  change_request: 'Änderung',
  feature_request: 'Feature',
};

type FeedbackEchoBrainPayload = {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  type: FeedbackType;
  category: FeedbackCategory;
  title: string;
  description: string;
  priority: FeedbackPriority;
  currentPage: string;
  appSection?: string;
  pageName?: string;
  screenshotUrl?: string;
};

export async function syncFeedbackToEchoBrain(data: FeedbackEchoBrainPayload): Promise<void> {
  const feedbackLabel = FEEDBACK_TYPE_LABELS[data.type];
  const submitter = data.userName || data.userEmail || 'Unbekannt';
  const sourcePage = [data.appSection, data.pageName].filter(Boolean).join(' > ') || data.currentPage;
  const taskDescription = [
    `Typ: ${feedbackLabel}`,
    `Kategorie: ${data.category}`,
    `Priorität: ${data.priority}`,
    `Quelle: ${sourcePage}`,
    `Gemeldet von: ${submitter}${data.userEmail ? ` (${data.userEmail})` : ''}`,
    data.screenshotUrl ? `Screenshot: ${data.screenshotUrl}` : undefined,
    '',
    data.description,
  ]
    .filter((line): line is string => line !== undefined)
    .join('\n');

  const checklistText = `[${data.priority.toUpperCase()}] ${feedbackLabel}: ${data.title}${
    data.description ? `\n${data.description}` : ''
  } (by ${submitter})`;

  const results = await Promise.allSettled([
    echoBrainService.postFeedback(data.type, checklistText, 'AIDELINE'),
    echoBrainService.syncTask({
      id: data.id,
      title: `[${feedbackLabel}] ${data.title}`,
      description: taskDescription,
      assigned_to: data.userId,
      assigned_to_name: submitter,
      priority: data.priority,
      category: data.type,
      source: 'AIDELINE',
    }),
  ]);

  const failures = results.filter((result) => result.status === 'rejected');
  if (failures.length > 0) {
    failures.forEach((failure) => {
      if (failure.status === 'rejected') {
        console.warn('[EchoBrain] Feedback sync failed:', failure.reason);
      }
    });
    throw new Error('Echo-Brain feedback sync failed');
  }
}

export async function submitFeedbackWithEchoBrain(data: {
  orgId: string;
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string;
  type: FeedbackType;
  category: FeedbackCategory;
  title: string;
  description: string;
  priority: FeedbackPriority;
  currentPage: string;
  screenshotUrl?: string;
}): Promise<string> {
  // 1. Post to Firebase (existing AIDELINE flow)
  const id = await feedbackService.submitFeedback(data);

  // 2. Also post to Echo-Brain (best-effort, never blocks the main flow)
  syncFeedbackToEchoBrain({ id, ...data })
    .catch((err) => {
      // Silent fail — Echo-Brain sync is best-effort
      console.warn('[EchoBrain] Failed to sync feedback:', err.message);
    });

  return id;
}
